function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function hydrateDeferredHtmlContainers(scope = document) {
  if (!scope?.querySelectorAll) return;

  scope.querySelectorAll('[data-deferred-html-container], [data-modal-deferred-html-container]').forEach((container) => {
    const isModalContainer = container.hasAttribute('data-modal-deferred-html-container');
    const loadedFlag = isModalContainer ? 'modalDeferredHtmlLoaded' : 'deferredHtmlLoaded';
    if (container.dataset[loadedFlag] === 'true') return;

    const template = container.querySelector(
      isModalContainer ? 'template[data-modal-deferred-html-template]' : 'template[data-deferred-html-template]'
    );
    if (!template) return;

    container.dataset[loadedFlag] = 'true';
    const content = template.content.cloneNode(true);
    template.remove();
    container.appendChild(content);
  });
}

function hydrateCardDeferredImage(image) {
  if (!image || image.dataset.cardDeferredHydrated === 'true') return;

  const src = image.getAttribute('data-src');
  const srcset = image.getAttribute('data-srcset');
  const sizes = image.getAttribute('data-sizes');

  if (srcset) image.setAttribute('srcset', srcset);
  if (sizes) image.setAttribute('sizes', sizes);
  if (src) image.setAttribute('src', src);

  image.dataset.cardDeferredHydrated = 'true';
  image.removeAttribute('data-src');
  image.removeAttribute('data-srcset');
  image.removeAttribute('data-sizes');
}

function initCardSecondaryImageHydration() {
  if (window.__cardSecondaryImageHydrationBound) return;
  window.__cardSecondaryImageHydrationBound = true;

  const selector = 'img[data-card-secondary-image="true"][data-src]';

  const hydrateInScope = (scope) => {
    if (!scope?.querySelectorAll) return;
    scope.querySelectorAll(selector).forEach(hydrateCardDeferredImage);
  };

  const hydrateFromIntent = (event) => {
    const card = event.target?.closest?.('.card-wrapper');
    if (!card) return;
    hydrateInScope(card);
  };

  const observeNearViewport = () => {
    const images = Array.from(document.querySelectorAll(selector));
    if (!images.length || typeof IntersectionObserver !== 'function') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          hydrateCardDeferredImage(entry.target);
        });
      },
      { rootMargin: '500px 0px' }
    );

    images.forEach((image) => observer.observe(image));
  };

  const scheduleObserver = () => {
    const shouldPreloadHoverMedia =
      typeof window.matchMedia !== 'function' || window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!shouldPreloadHoverMedia) return;

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(observeNearViewport, { timeout: 2500 });
    } else {
      window.setTimeout(observeNearViewport, 1200);
    }
  };

  document.addEventListener('pointerover', hydrateFromIntent, { passive: true });
  document.addEventListener('focusin', hydrateFromIntent);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleObserver, { once: true });
  } else {
    scheduleObserver();
  }
}

initCardSecondaryImageHydration();

document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    hydrateDeferredHtmlContainers(details);
  },
  true
);

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('details[open]').forEach((details) => hydrateDeferredHtmlContainers(details));
});

function updateCartIconBubbleCount(nextCartIconMarkup) {
  const cartIconBubble = document.getElementById('cart-icon-bubble');
  if (!cartIconBubble) return;

  const source = document.createElement('div');
  source.innerHTML = nextCartIconMarkup || '';
  const hasItems = !!source.querySelector('.cart-count-bubble');
  const desiredTemplate = cartIconBubble.querySelector(
    `template[data-cart-icon-template="${hasItems ? 'filled' : 'empty'}"]`
  );
  const desiredIcon = desiredTemplate?.content?.firstElementChild?.cloneNode(true);
  if (desiredIcon) {
    const currentIcon = cartIconBubble.querySelector('.cart-icon-bubble__icon');
    if (currentIcon) {
      currentIcon.replaceWith(desiredIcon);
    } else {
      cartIconBubble.insertBefore(desiredIcon, cartIconBubble.firstChild);
    }
  }

  const currentCountBubble = cartIconBubble.querySelector('.cart-count-bubble');
  if (currentCountBubble) currentCountBubble.remove();
  const nextCountBubble = source.querySelector('.cart-count-bubble');
  if (nextCountBubble) cartIconBubble.appendChild(nextCountBubble);
}

window.updateCartIconBubbleCount = updateCartIconBubbleCount;

const THEME_HAPTIC_PATTERNS = Object.freeze({
  tap: 14,
  selection: 6,
  success: [10, 30, 12],
  error: [14, 35, 14],
});

function supportsVibrationApi() {
  return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
}

function supportsIosSwitchHapticsFallback() {
  if (typeof document === 'undefined') return false;
  if (!isIOSDevice()) return false;
  return true;
}

function normalizeHapticFlag(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value === 'true';
  return fallback;
}

class ThemeHaptics {
  constructor(settings = {}) {
    this.enabled = normalizeHapticFlag(settings.enabled, false);
    this.buttonsEnabled = normalizeHapticFlag(settings.buttons, true);
    this.variantInputsEnabled = normalizeHapticFlag(settings.variantInputs, true);
    this.quantityInputsEnabled = normalizeHapticFlag(settings.quantityInputs, true);
    this.checkoutEnabled = normalizeHapticFlag(settings.checkout, true);
    this.cartActionsEnabled = normalizeHapticFlag(settings.cartActions, true);
    this.iosFallbackEnabled = normalizeHapticFlag(settings.iosFallback, false);
    this.supported = supportsVibrationApi();
    this.supportsIosFallback = this.iosFallbackEnabled && supportsIosSwitchHapticsFallback();
    this.minGapMs = 50;
    this.lastPulseAt = 0;
    this.variantInputMinGapMs = 120;
    this.lastVariantInputPulseAt = 0;
    this.quantityMinGapMs = 90;
    this.lastQuantityPulseAt = 0;
    this.lastPressAt = 0;
    this.clickFallbackWindowMs = 450;
    this.initialized = false;
  }

  isAddToCartButton(element) {
    if (element.matches('button[name="checkout"], .cart__checkout-button, [data-cart-drawer-action="checkout"]')) {
      return false;
    }
    return (
      element.matches('form[action*="/cart/add"] button[type="submit"]:not([name="checkout"])') ||
      element.matches('.product-form__submit:not([name="checkout"])') ||
      element.matches('.quick-add__submit') ||
      element.matches('[data-cart-change-action="add"], [data-cart-change-action="update"]')
    );
  }

  isCheckoutButton(element) {
    return !!element.closest('button[name="checkout"], .cart__checkout-button, [data-cart-drawer-action="checkout"]');
  }

  usesIosFallbackOnly() {
    return this.supportsIosFallback && !this.supported;
  }

  getVariantInputControl(target) {
    if (!(target instanceof Element)) return null;

    const variantRoot = target.closest('variant-selects');
    if (!variantRoot) return null;

    const directControl = target.closest('select, input[type="radio"], input[type="checkbox"]');
    if (directControl && variantRoot.contains(directControl)) return directControl;

    const label = target.closest('label');
    if (!label || !variantRoot.contains(label)) return null;

    if (label.control) return label.control;

    const controlId = label.getAttribute('for');
    if (!controlId) return null;

    const control = document.getElementById(controlId);
    return control && variantRoot.contains(control) ? control : null;
  }

  isEnabledVariantInputTarget(target) {
    const control = this.getVariantInputControl(target);
    if (!control) return false;
    return !control.disabled && control.getAttribute('aria-disabled') !== 'true';
  }

  pulse(pattern, force = false) {
    if (!this.enabled) return false;
    const now = Date.now();
    if (!force && now - this.lastPulseAt < this.minGapMs) return false;
    this.lastPulseAt = now;

    let vibrateSucceeded = false;
    if (this.supported) {
      try {
        vibrateSucceeded = navigator.vibrate(pattern) === true;
      } catch (error) {
        vibrateSucceeded = false;
      }
    }

    if (vibrateSucceeded) return true;

    if (this.supportsIosFallback) {
      return this.pulseWithIosFallback(pattern);
    }

    return false;
  }

  schedulePulse(pattern, force = false, immediate = false) {
    if (!this.enabled) return;

    const run = () => this.pulse(pattern, force);

    if (immediate || this.usesIosFallbackOnly() || typeof window === 'undefined') {
      run();
      return;
    }

    if (typeof queueMicrotask === 'function') {
      queueMicrotask(run);
      return;
    }

    window.setTimeout(run, 0);
  }

  scheduleQuantityPulse(immediate = false) {
    if (!this.enabled || !this.quantityInputsEnabled) return;
    const now = Date.now();
    if (now - this.lastQuantityPulseAt < this.quantityMinGapMs) return;
    this.lastQuantityPulseAt = now;
    this.schedulePulse(THEME_HAPTIC_PATTERNS.selection, false, immediate);
  }

  scheduleVariantInputPulse(immediate = false) {
    if (!this.enabled || !this.variantInputsEnabled) return;
    const now = Date.now();
    if (now - this.lastVariantInputPulseAt < this.variantInputMinGapMs) return;
    this.lastVariantInputPulseAt = now;
    this.schedulePulse(THEME_HAPTIC_PATTERNS.selection, false, immediate);
  }

  ensureIosFallbackElements() {
    if (typeof document === 'undefined' || !document.body) return false;
    return true;
  }

  pulseWithIosFallback(pattern) {
    if (!this.ensureIosFallbackElements()) return false;
    const pulseCount = 1;

    for (let i = 0; i < pulseCount; i += 1) {
      const wrapper = document.createElement('div');
      wrapper.setAttribute('aria-hidden', 'true');
      wrapper.style.cssText =
        'position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.setAttribute('switch', '');
      const label = document.createElement('label');
      label.appendChild(input);
      wrapper.appendChild(label);
      document.body.appendChild(wrapper);

      label.click();
      window.setTimeout(() => {
        if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
      }, 120);
    }

    return true;
  }

  getEventElement(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : null;
    const primaryTarget = path && path.length ? path[0] : event.target;
    if (primaryTarget instanceof Element) return primaryTarget;
    if (primaryTarget instanceof Node) return primaryTarget.parentElement;
    return null;
  }

  handlePress = (target, { immediate = false } = {}) => {
    if (!(target instanceof Element)) return;
    if (target.closest('[data-haptic="off"]')) return;

    const trigger = target.closest('button, .button, [role="button"]');
    const quantityTrigger = target.closest('quantity-input .quantity__button');

    if (this.variantInputsEnabled && this.isEnabledVariantInputTarget(target)) {
      this.scheduleVariantInputPulse(immediate);
      return;
    }

    if (this.quantityInputsEnabled && quantityTrigger) {
      if (quantityTrigger.disabled || quantityTrigger.getAttribute('aria-disabled') === 'true') return;
      this.scheduleQuantityPulse(immediate);
      return;
    }

    if (!trigger) return;
    if (trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') return;

    if (this.variantInputsEnabled && trigger.matches('.card__swatch')) {
      this.schedulePulse(THEME_HAPTIC_PATTERNS.selection, false, immediate);
      return;
    }

    if (this.buttonsEnabled && this.isAddToCartButton(trigger)) {
      this.schedulePulse(THEME_HAPTIC_PATTERNS.tap, false, immediate);
      return;
    }

    if (this.checkoutEnabled && this.isCheckoutButton(trigger)) {
      this.schedulePulse(THEME_HAPTIC_PATTERNS.tap, false, immediate);
    }
  };

  handlePointerDown = (event) => {
    if (!this.enabled) return;
    if (event.button !== 0) return;
    this.lastPressAt = Date.now();
    if (this.usesIosFallbackOnly()) return;
    this.handlePress(this.getEventElement(event), { immediate: true });
  };

  handleTouchStart = (event) => {
    if (!this.enabled) return;
    this.lastPressAt = Date.now();
    if (this.usesIosFallbackOnly()) return;
    this.handlePress(this.getEventElement(event), { immediate: true });
  };

  handleClick = (event) => {
    if (!this.enabled) return;
    const now = Date.now();
    if (event.detail !== 0 && now - this.lastPressAt < this.clickFallbackWindowMs && !this.usesIosFallbackOnly()) return;
    this.handlePress(this.getEventElement(event), { immediate: false });
  };

  handleSubmit = (event) => {
    if (!this.enabled) return;
    const form = event.target instanceof Element ? event.target.closest('form') : null;
    if (!form) return;

    const submitter = event.submitter instanceof Element ? event.submitter : null;

    if (
      this.buttonsEnabled &&
      (form.matches('form[action*="/cart/add"]') ||
        submitter?.matches('.product-form__submit:not([name="checkout"]), .quick-add__submit, [data-cart-change-action="add"], [data-cart-change-action="update"]'))
    ) {
      this.schedulePulse(THEME_HAPTIC_PATTERNS.tap, false, true);
      return;
    }

    if (this.checkoutEnabled && (submitter?.matches('button[name="checkout"], .cart__checkout-button') || form.querySelector('button[name="checkout"]'))) {
      this.schedulePulse(THEME_HAPTIC_PATTERNS.tap, false, true);
    }
  };

  handleChange = (event) => {
    if (!this.variantInputsEnabled) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-haptic="off"]')) return;

    const isVariantControl =
      event.target.matches('variant-selects select, variant-selects input[type="radio"], variant-selects input[type="checkbox"]') ||
      !!event.target.closest('variant-selects');

    if (isVariantControl) {
      this.scheduleVariantInputPulse(false);
    }
  };

  handleOptionValueSelectionChange = () => {
    if (!this.variantInputsEnabled) return;
    this.scheduleVariantInputPulse(false);
  };

  handleCartUpdate = (event) => {
    if (!this.cartActionsEnabled) return;
    if (this.supportsIosFallback && !this.supported) return;
    const source = event?.source || '';
    if (!source) return;
    if (!['product-form', 'cart-change', 'quick-add'].includes(source)) return;
    this.schedulePulse(THEME_HAPTIC_PATTERNS.success, true, false);
  };

  handleCartError = () => {
    if (!this.cartActionsEnabled) return;
    if (this.supportsIosFallback && !this.supported) return;
    this.schedulePulse(THEME_HAPTIC_PATTERNS.error, true, false);
  };

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (!this.enabled || (!this.supported && !this.supportsIosFallback)) return;
    if (document.documentElement.classList.contains('shopify-design-mode')) return;

    document.addEventListener('pointerdown', this.handlePointerDown, { capture: true, passive: true });
    document.addEventListener('touchstart', this.handleTouchStart, { capture: true, passive: true });
    document.addEventListener('click', this.handleClick, { capture: true, passive: true });
    document.addEventListener('submit', this.handleSubmit, { capture: true });
    document.addEventListener('change', this.handleChange, { capture: true });

    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionValueSelectionChange);
      subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
      subscribe(PUB_SUB_EVENTS.cartError, this.handleCartError);
    }
  }
}

function initThemeHaptics() {
  const settings = window.themeHapticsSettings || {};
  const manager = new ThemeHaptics(settings);
  manager.init();
  window.themeHapticsManager = manager;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initThemeHaptics, { once: true });
} else {
  initThemeHaptics();
}

const bodyScrollLockState = {
  count: 0,
  scrollY: 0,
  scrollX: 0,
  useFixedBodyLock: true,
  touchStartX: 0,
  touchStartY: 0,
  hadOverflowHiddenClass: false,
  htmlStyles: null,
  bodyStyles: null,
  scrollListener: null,
  enforceFrame: null,
  touchStartListener: null,
  touchMoveListener: null,
  wheelListener: null,
};

function isIOSDevice() {
  const ua = window.navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

function getActiveModalScrollRoot(targetElement) {
  const activeRootSelectors = [
    'quick-add-modal[open] .quick-add-modal__content',
    '.mobile-facets__disclosure[open] .mobile-facets__inner',
    '.facets__disclosure--inline[open] > .facets__display',
    'modal-dialog[open] .product-popup-modal__content',
    'modal-dialog[open] .product-media-modal__content',
    'details-modal details[open] .modal__content',
    'pickup-availability-drawer[open]',
    'cart-drawer.active .drawer__inner',
  ];

  const activeRoots = activeRootSelectors.flatMap((selector) => Array.from(document.querySelectorAll(selector)));
  if (!activeRoots.length) return null;

  if (targetElement instanceof Element) {
    let node = targetElement;
    while (node) {
      if (activeRoots.includes(node)) return node;
      node = node.parentElement;
    }
  }

  return activeRoots[0];
}

function isScrollableNode(node, axis = 'y') {
  if (!(node instanceof HTMLElement)) return false;
  const styles = window.getComputedStyle(node);
  if (axis === 'x') {
    const overflowX = styles.overflowX;
    const canOverflowX = overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay';
    return canOverflowX && node.scrollWidth > node.clientWidth + 1;
  }

  const overflowY = styles.overflowY;
  const canOverflowY = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
  return canOverflowY && node.scrollHeight > node.clientHeight + 1;
}

function findScrollableNode(startNode, boundaryNode, axis = 'y') {
  let node = startNode;
  while (node && node !== boundaryNode) {
    if (isScrollableNode(node, axis)) return node;
    node = node.parentElement;
  }
  return isScrollableNode(boundaryNode, axis) ? boundaryNode : null;
}

function canScrollNode(node, delta, axis = 'y') {
  if (!(node instanceof HTMLElement)) return false;
  if (axis === 'x') {
    if (delta < 0) return node.scrollLeft > 0;
    if (delta > 0) return node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
    return true;
  }
  if (delta < 0) return node.scrollTop > 0;
  if (delta > 0) return node.scrollTop + node.clientHeight < node.scrollHeight - 1;
  return true;
}

function shouldPreventBackgroundScroll(targetElement, deltas = {}) {
  const root = getActiveModalScrollRoot(targetElement);
  if (!root) return false;
  if (!(targetElement instanceof Element)) return true;
  if (!root.contains(targetElement)) return true;

  const deltaX = Number(deltas.deltaX || 0);
  const deltaY = Number(deltas.deltaY || 0);
  const axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
  const primaryDelta = axis === 'x' ? deltaX : deltaY;
  const scrollContainer = findScrollableNode(targetElement, root, axis);
  if (!scrollContainer) return true;
  if (primaryDelta === 0) return false;

  return !canScrollNode(scrollContainer, primaryDelta, axis);
}

function enforceLockedScrollPosition() {
  if (bodyScrollLockState.count === 0) {
    bodyScrollLockState.enforceFrame = null;
    return;
  }

  const currentY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
  const currentX = window.scrollX || window.pageXOffset || 0;
  if (
    Math.abs(currentY - (bodyScrollLockState.scrollY || 0)) > 1 ||
    Math.abs(currentX - (bodyScrollLockState.scrollX || 0)) > 1
  ) {
    window.scrollTo(bodyScrollLockState.scrollX || 0, bodyScrollLockState.scrollY || 0);
  }

  bodyScrollLockState.enforceFrame = requestAnimationFrame(enforceLockedScrollPosition);
}

function lockBodyScroll() {
  const body = document.body;
  const html = document.documentElement;
  if (!body || !html) return;

  if (bodyScrollLockState.count === 0) {
    bodyScrollLockState.useFixedBodyLock = !isIOSDevice();
    bodyScrollLockState.scrollY = window.scrollY || window.pageYOffset || html.scrollTop || 0;
    bodyScrollLockState.scrollX = window.scrollX || window.pageXOffset || 0;
    bodyScrollLockState.hadOverflowHiddenClass = body.classList.contains('overflow-hidden');
    bodyScrollLockState.htmlStyles = {
      overflow: html.style.overflow,
      overscrollBehavior: html.style.overscrollBehavior,
    };
    bodyScrollLockState.bodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      touchAction: body.style.touchAction,
      overscrollBehavior: body.style.overscrollBehavior,
      webkitOverflowScrolling: body.style.webkitOverflowScrolling,
    };

    body.classList.add('overflow-hidden');
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    if (bodyScrollLockState.useFixedBodyLock) {
      body.style.position = 'fixed';
      body.style.top = `-${bodyScrollLockState.scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.touchAction = 'none';
      body.style.webkitOverflowScrolling = 'auto';
    } else {
      body.style.touchAction = bodyScrollLockState.bodyStyles?.touchAction || '';
      body.style.webkitOverflowScrolling = bodyScrollLockState.bodyStyles?.webkitOverflowScrolling || '';

      if (!bodyScrollLockState.touchStartListener) {
        bodyScrollLockState.touchStartListener = (event) => {
          if (bodyScrollLockState.count === 0 || !event.touches || event.touches.length === 0) return;
          bodyScrollLockState.touchStartX = event.touches[0].clientX;
          bodyScrollLockState.touchStartY = event.touches[0].clientY;
        };
      }

      if (!bodyScrollLockState.touchMoveListener) {
        bodyScrollLockState.touchMoveListener = (event) => {
          if (bodyScrollLockState.count === 0 || !event.cancelable) return;
          if (!event.touches || event.touches.length !== 1) return;

          const currentX = event.touches[0].clientX;
          const currentY = event.touches[0].clientY;
          const previousX = bodyScrollLockState.touchStartX || currentX;
          const previousY = bodyScrollLockState.touchStartY || currentY;
          const deltaX = previousX - currentX;
          const deltaY = previousY - currentY;
          bodyScrollLockState.touchStartX = currentX;
          bodyScrollLockState.touchStartY = currentY;

          if (shouldPreventBackgroundScroll(event.target, { deltaX, deltaY })) {
            event.preventDefault();
          }
        };
      }

      if (!bodyScrollLockState.wheelListener) {
        bodyScrollLockState.wheelListener = (event) => {
          if (bodyScrollLockState.count === 0 || !event.cancelable) return;
          if (
            shouldPreventBackgroundScroll(event.target, {
              deltaX: event.deltaX || 0,
              deltaY: event.deltaY || 0,
            })
          ) {
            event.preventDefault();
          }
        };
      }

      document.addEventListener('touchstart', bodyScrollLockState.touchStartListener, { passive: true });
      document.addEventListener('touchmove', bodyScrollLockState.touchMoveListener, { passive: false });
      document.addEventListener('wheel', bodyScrollLockState.wheelListener, { passive: false });
    }
    window.scrollTo(bodyScrollLockState.scrollX || 0, bodyScrollLockState.scrollY || 0);

    if (!bodyScrollLockState.scrollListener) {
      bodyScrollLockState.scrollListener = () => {
        if (bodyScrollLockState.count === 0) return;
        const currentY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
        if (Math.abs(currentY - bodyScrollLockState.scrollY) > 1) {
          window.scrollTo(bodyScrollLockState.scrollX || 0, bodyScrollLockState.scrollY || 0);
        }
      };
    }
    window.addEventListener('scroll', bodyScrollLockState.scrollListener, { passive: true });

    if (bodyScrollLockState.enforceFrame) cancelAnimationFrame(bodyScrollLockState.enforceFrame);
    bodyScrollLockState.enforceFrame = requestAnimationFrame(enforceLockedScrollPosition);
  }

  bodyScrollLockState.count += 1;
}

function unlockBodyScroll() {
  const body = document.body;
  const html = document.documentElement;
  if (!body || !html || bodyScrollLockState.count === 0) return;

  bodyScrollLockState.count -= 1;
  if (bodyScrollLockState.count > 0) return;

  const restoreStyles = bodyScrollLockState.bodyStyles || {};
  body.style.position = restoreStyles.position || '';
  body.style.top = restoreStyles.top || '';
  body.style.left = restoreStyles.left || '';
  body.style.right = restoreStyles.right || '';
  body.style.width = restoreStyles.width || '';
  body.style.overflow = restoreStyles.overflow || '';
  body.style.touchAction = restoreStyles.touchAction || '';
  body.style.overscrollBehavior = restoreStyles.overscrollBehavior || '';
  body.style.webkitOverflowScrolling = restoreStyles.webkitOverflowScrolling || '';

  const restoreHtmlStyles = bodyScrollLockState.htmlStyles || {};
  html.style.overflow = restoreHtmlStyles.overflow || '';
  html.style.overscrollBehavior = restoreHtmlStyles.overscrollBehavior || '';

  if (!bodyScrollLockState.hadOverflowHiddenClass) {
    body.classList.remove('overflow-hidden');
  }

  if (bodyScrollLockState.scrollListener) {
    window.removeEventListener('scroll', bodyScrollLockState.scrollListener);
  }
  if (bodyScrollLockState.enforceFrame) {
    cancelAnimationFrame(bodyScrollLockState.enforceFrame);
    bodyScrollLockState.enforceFrame = null;
  }
  if (bodyScrollLockState.touchStartListener) {
    document.removeEventListener('touchstart', bodyScrollLockState.touchStartListener);
  }
  if (bodyScrollLockState.touchMoveListener) {
    document.removeEventListener('touchmove', bodyScrollLockState.touchMoveListener);
  }
  if (bodyScrollLockState.wheelListener) {
    document.removeEventListener('wheel', bodyScrollLockState.wheelListener);
  }
  bodyScrollLockState.touchStartX = 0;
  bodyScrollLockState.touchStartY = 0;

  window.scrollTo(bodyScrollLockState.scrollX || 0, bodyScrollLockState.scrollY || 0);

  bodyScrollLockState.scrollX = 0;
  bodyScrollLockState.scrollY = 0;
  bodyScrollLockState.hadOverflowHiddenClass = false;
  bodyScrollLockState.htmlStyles = null;
  bodyScrollLockState.bodyStyles = null;
}

window.lockBodyScroll = lockBodyScroll;
window.unlockBodyScroll = unlockBodyScroll;

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function focusElementWithoutScroll(element) {
  if (!element || typeof element.focus !== 'function') return;

  try {
    element.focus({ preventScroll: true });
  } catch (error) {
    element.focus();
  }
}

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      focusElementWithoutScroll(first);
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      focusElementWithoutScroll(last);
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  focusElementWithoutScroll(elementToFocus);

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (this.classList.contains('cart-quantity') && event.target.name === 'minus') {
      const currentValue = parseInt(this.input.value);
      if (!Number.isNaN(currentValue) && currentValue <= 1) {
        const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
        if (cartItems) {
          cartItems.dispatchEvent(
            new CustomEvent('cart:remove-requested', {
              bubbles: true,
              detail: {
                line: this.input.dataset.index,
                trigger: event.currentTarget,
              },
            })
          );
          return;
        }
      }
    }

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }

    if (this.classList.contains('cart-quantity')) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      const showTrash = !Number.isNaN(value) && value <= 1;
      buttonMinus.classList.toggle('quantity__button--trash', showTrash);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents(scope = this) {
    const summaries = scope.matches?.('summary') ? [scope] : scope.querySelectorAll?.('summary') || [];
    summaries.forEach((summary) => {
      if (summary.dataset.menuDrawerBound === 'true') return;
      summary.dataset.menuDrawerBound = 'true';
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    });

    const buttons = scope.matches?.(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    )
      ? [scope]
      : scope.querySelectorAll?.(
          'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
        ) || [];

    buttons.forEach((button) => {
      if (button.dataset.menuDrawerBound === 'true') return;
      button.dataset.menuDrawerBound = 'true';
      button.addEventListener('click', this.onCloseButtonClick.bind(this));
    });
  }

  hydrateDeferredTemplate(container, templateType = '') {
    if (!container?.children) return null;

    const template = Array.from(container.children).find((child) => {
      if (child.tagName !== 'TEMPLATE' || !child.hasAttribute('data-menu-drawer-deferred-template')) return false;
      if (!templateType) return true;
      return child.dataset.menuDrawerDeferredTemplate === templateType;
    });

    if (!template) return null;

    const fragment = template.content.cloneNode(true);
    template.replaceWith(fragment);
    this.bindEvents(container);
    return true;
  }

  onSubmenuOpen(_detailsElement) {}

  onSubmenuClosed(_detailsElement) {}

  getSubmenuElement(summaryElement) {
    const detailsElement = summaryElement?.parentNode;
    if (!detailsElement) return null;

    this.hydrateDeferredTemplate(detailsElement, 'submenu');
    return summaryElement.nextElementSibling;
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const submenuElement = detailsElement === this.mainDetailsToggle ? this.mainDetailsToggle : this.getSubmenuElement(summaryElement);

    function addTrapFocus() {
      if (!submenuElement) return;
      trapFocus(submenuElement, detailsElement.querySelector('button'));
      submenuElement.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      this.onSubmenuOpen(detailsElement);
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : submenuElement?.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        this.onSubmenuClosed(detailsElement);
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.menuDrawerRoot = this.querySelector('#menu-drawer');
    this.defaultDesktopAsideMarkup = '';
    this.dynamicListsSelector = '.menu-drawer__dynamic-lists';
    this.dynamicListsNeedsRefresh = true;
    this.dynamicListsLastRefreshToken = '';
    this.dynamicListsRefreshInFlight = null;
    this.drawerContentLoadPromise = null;
    this.onLastViewedSynced = this.onLastViewedSynced.bind(this);
    this.prefetchDeferredDrawerContent = this.prefetchDeferredDrawerContent.bind(this);
    document.addEventListener('last-viewed:cart-attributes-synced', this.onLastViewedSynced);
    this.applyTopLinkOverrides();
    this.scheduleDeferredDrawerPrefetch();
  }

  buildHeaderSectionRequestUrl(sectionId, options = {}) {
    const rootPath = window.Shopify?.routes?.root || '/';
    const normalizedRoot = rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
    const requestUrl = new URL(`${normalizedRoot}search`, window.location.origin);
    requestUrl.searchParams.set('section_id', sectionId);
    if (options.light) requestUrl.searchParams.set('q', '__rpd_header_drawer_light__');
    return requestUrl;
  }

  appendCurrentDrawerParams(requestUrl) {
    if (!requestUrl) return;
    try {
      const currentParams = new URLSearchParams(window.location.search);
      ['rpd_p1', 'rpd_p2', 'rpd_p3', 'rpd_p4', 'rpd_p5', 'rpd_lvh'].forEach((key) => {
        const value = currentParams.get(key);
        if (value) requestUrl.searchParams.set(key, value);
      });
    } catch (error) {}
  }

  appendStoredDrawerParams(requestUrl) {
    if (!requestUrl) return;
    const lastViewedHandles = this.getLastViewedHandlesFromStorage();
    if (lastViewedHandles) requestUrl.searchParams.set('rpd_lvh', lastViewedHandles);
  }

  shouldPrefetchDeferredDrawerContent() {
    const menuDrawer = this.menuDrawerRoot || this.querySelector('#menu-drawer');
    if (!menuDrawer || menuDrawer.dataset.rpdDeferredDrawerContent !== 'true') return false;
    if (menuDrawer.dataset.rpdDeferredDrawerLoaded === 'true') return false;

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return true;
    if (connection.saveData) return false;
    if (connection.effectiveType && /(^|-)2g$/.test(connection.effectiveType)) return false;

    return true;
  }

  scheduleDeferredDrawerPrefetch() {
    if (!this.shouldPrefetchDeferredDrawerContent()) return;

    const schedulePrefetch = () => {
      const isDesktopDrawer = this.isDesktopDrawerViewport();
      const delay = isDesktopDrawer ? 4500 : 1800;
      const idleTimeout = isDesktopDrawer ? 7000 : 3000;

      window.setTimeout(() => {
        if (!this.shouldPrefetchDeferredDrawerContent()) return;

        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(this.prefetchDeferredDrawerContent, { timeout: idleTimeout });
        } else {
          this.prefetchDeferredDrawerContent();
        }
      }, delay);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', schedulePrefetch, { once: true });
    } else {
      schedulePrefetch();
    }
  }

  prefetchDeferredDrawerContent() {
    if (!this.shouldPrefetchDeferredDrawerContent()) return;
    this.ensureDeferredDrawerContent().catch(() => {});
  }

  ensureDeferredDrawerContent() {
    const menuDrawer = this.menuDrawerRoot || this.querySelector('#menu-drawer');
    if (!menuDrawer || menuDrawer.dataset.rpdDeferredDrawerContent !== 'true') return Promise.resolve(true);
    if (menuDrawer.dataset.rpdDeferredDrawerLoaded === 'true') return Promise.resolve(true);
    if (this.drawerContentLoadPromise) return this.drawerContentLoadPromise;

    const sectionId = menuDrawer.dataset.sectionId;
    if (!sectionId) return Promise.resolve(false);

    const requestUrl = this.buildHeaderSectionRequestUrl(sectionId, { light: !this.isDesktopDrawerViewport() });
    requestUrl.searchParams.set('rpd_header_drawer_content', '1');
    this.appendCurrentDrawerParams(requestUrl);
    this.appendStoredDrawerParams(requestUrl);

    menuDrawer.dataset.rpdDeferredDrawerLoading = 'true';

    this.drawerContentLoadPromise = fetch(requestUrl.toString(), { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to load menu drawer content (${response.status})`);
        return response.text();
      })
      .then((markup) => {
        const nextDocument = new DOMParser().parseFromString(markup, 'text/html');
        const nextMenuDrawer = nextDocument.querySelector('#menu-drawer');
        if (!nextMenuDrawer) return false;

        Array.from(menuDrawer.attributes).forEach((attribute) => {
          if (attribute.name === 'id') return;
          menuDrawer.removeAttribute(attribute.name);
        });

        Array.from(nextMenuDrawer.attributes).forEach((attribute) => {
          menuDrawer.setAttribute(attribute.name, attribute.value);
        });

        menuDrawer.innerHTML = nextMenuDrawer.innerHTML;
        menuDrawer.dataset.rpdDeferredDrawerLoaded = 'true';
        menuDrawer.dataset.rpdDeferredDrawerContent = 'false';
        this.dynamicListsNeedsRefresh = false;
        this.dynamicListsLastRefreshToken = this.getDynamicListsRefreshToken();
        menuDrawer.removeAttribute('data-rpd-deferred-drawer-loading');
        this.menuDrawerRoot = menuDrawer;
        this.bindEvents(menuDrawer);
        this.applyTopLinkOverrides(menuDrawer);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        menuDrawer.removeAttribute('data-rpd-deferred-drawer-loading');
        this.drawerContentLoadPromise = null;
      });

    return this.drawerContentLoadPromise;
  }

  disconnectedCallback() {
    document.removeEventListener('last-viewed:cart-attributes-synced', this.onLastViewedSynced);
    window.removeEventListener('resize', this.onResize);
  }

  isDesktopDrawerViewport() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  getDesktopAsideContainer() {
    return this.menuDrawerRoot?.querySelector('[data-menu-drawer-desktop-aside="true"]') || null;
  }

  cacheDefaultDesktopAside() {
    const aside = this.getDesktopAsideContainer();
    if (!aside || this.defaultDesktopAsideMarkup) return;
    this.defaultDesktopAsideMarkup = aside.innerHTML;
  }

  animateDesktopAsidePanel(aside) {
    const panel = aside?.querySelector?.('.menu-drawer__desktop-panel');
    if (!panel || !panel.classList.contains('menu-drawer__desktop-panel--products')) return;

    panel.classList.add('is-entering');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.classList.remove('is-entering');
      });
    });
  }

  restoreDefaultDesktopAside() {
    const aside = this.getDesktopAsideContainer();
    if (!aside || !this.defaultDesktopAsideMarkup) return;
    aside.innerHTML = this.defaultDesktopAsideMarkup;
    this.bindEvents(aside);
    this.animateDesktopAsidePanel(aside);
  }

  updateDesktopAsideForSubmenu(detailsElement) {
    if (!this.isDesktopDrawerViewport()) return;

    const aside = this.getDesktopAsideContainer();
    if (!aside) return;

    this.cacheDefaultDesktopAside();

    const panelTemplate = detailsElement?.querySelector?.('template[data-menu-drawer-desktop-aside-template]');
    if (!panelTemplate?.content) {
      this.restoreDefaultDesktopAside();
      return;
    }

    aside.innerHTML = '';
    aside.appendChild(panelTemplate.content.cloneNode(true));
    this.bindEvents(aside);
    this.animateDesktopAsidePanel(aside);
  }

  getDynamicListsRefreshToken() {
    try {
      return window.sessionStorage.getItem('last_viewed_handles_synced') || '';
    } catch (error) {
      return '';
    }
  }

  onLastViewedSynced(event) {
    if (!this.menuDrawerRoot || this.menuDrawerRoot.dataset.refreshDynamicLists !== 'true') return;

    const nextToken = event?.detail?.handles ? String(event.detail.handles) : '';
    this.dynamicListsNeedsRefresh = true;

    if (this.mainDetailsToggle?.hasAttribute('open')) {
      this.refreshDynamicLists({ force: true, refreshToken: nextToken });
    }
  }

  getNormalizedUrlParamValue(key) {
    if (!key || key === 'none') return '';

    try {
      const currentParams = window.rpdUrlParams?.current;
      if (currentParams && Object.prototype.hasOwnProperty.call(currentParams, key)) {
        const currentValue = currentParams[key];
        return currentValue ? String(currentValue).trim().toLowerCase() : '';
      }

      const value = new URLSearchParams(window.location.search).get(key);
      return value ? value.trim().toLowerCase() : '';
    } catch (error) {
      return '';
    }
  }

  applyTopLinkOverrides(scope = this.menuDrawerRoot) {
    this.applyTopImageOverrides(scope);
    this.applyTopTextMenuOverrides(scope);
    this.applySecondaryMenuOverrides(scope);
    this.applyBottomMenuOverrides(scope);
  }

  applyTopImageOverrides(scope = this.menuDrawerRoot) {
    if (!scope) return;

    const applyToRoot = (root) => {
      const cards = root.querySelectorAll?.('[data-rpd-drawer-top-image-card="true"]') || [];
      cards.forEach((card) => {
        const key = card.dataset.rpdParamKey || '';
        const matchValue = (card.dataset.rpdParamMatch || '').trim().toLowerCase();
        if (!key || key === 'none' || !matchValue) return;

        const activeValue = this.getNormalizedUrlParamValue(key);
        if (!activeValue || activeValue !== matchValue) return;

        const overrideTitle = card.dataset.rpdOverrideTitle || '';
        const overrideLink = card.dataset.rpdOverrideLink || '';
        const overrideImageSrc = card.dataset.rpdOverrideImageSrc || '';
        const overrideImageSrcset = card.dataset.rpdOverrideImageSrcset || '';
        const overrideImageAlt = card.dataset.rpdOverrideImageAlt || '';

        if (overrideTitle) {
          card.querySelectorAll('.menu-item-with-media__title').forEach((titleNode) => {
            titleNode.textContent = overrideTitle;
          });
        }

        if (overrideLink) {
          card.setAttribute('href', overrideLink);
        }

        if (overrideImageSrc) {
          const image = card.querySelector('img');
          if (image) {
            image.setAttribute('src', overrideImageSrc);
            if (overrideImageSrcset) {
              image.setAttribute('srcset', overrideImageSrcset);
            }
            if (overrideImageAlt) {
              image.setAttribute('alt', overrideImageAlt);
            }
          }
        }
      });
    };

    const deferredTemplates = scope.querySelectorAll?.('template[data-menu-drawer-deferred-template]') || [];
    deferredTemplates.forEach((template) => {
      if (template?.content) applyToRoot(template.content);
    });
    applyToRoot(scope);
  }

  applyTopTextMenuOverrides(scope = this.menuDrawerRoot) {
    this.applyTemplateMenuOverrides(scope, {
      wrapperSelector: '[data-rpd-drawer-top-text-menu="true"]',
      templateSelector: 'template[data-rpd-drawer-top-text-menu-override="true"]',
      contentSelector: '.menu-drawer__top-link-list',
      includeTopLinksTemplate: true,
    });
  }

  applySecondaryMenuOverrides(scope = this.menuDrawerRoot) {
    this.applyTemplateMenuOverrides(scope, {
      wrapperSelector: '[data-rpd-drawer-secondary-menu="true"]',
      templateSelector: 'template[data-rpd-drawer-secondary-menu-override="true"]',
      contentSelector: '[data-rpd-drawer-secondary-menu-content="true"]',
    });
  }

  applyBottomMenuOverrides(scope = this.menuDrawerRoot) {
    this.applyTemplateMenuOverrides(scope, {
      wrapperSelector: '[data-rpd-drawer-bottom-menu="true"]',
      templateSelector: 'template[data-rpd-drawer-bottom-menu-override="true"]',
      contentSelector: '[data-rpd-drawer-bottom-menu-content="true"]',
    });
  }

  applyTemplateMenuOverrides(
    scope = this.menuDrawerRoot,
    { wrapperSelector = '', templateSelector = '', contentSelector = '', includeTopLinksTemplate = false } = {}
  ) {
    if (!scope) return;
    if (!wrapperSelector || !templateSelector || !contentSelector) return;

    const applyToRoot = (root) => {
      const menus = root.querySelectorAll?.(wrapperSelector) || [];
      menus.forEach((menu) => {
        const key = menu.dataset.rpdParamKey || '';
        const matchValue = (menu.dataset.rpdParamMatch || '').trim().toLowerCase();
        if (!key || key === 'none' || !matchValue) return;

        const activeValue = this.getNormalizedUrlParamValue(key);
        if (!activeValue || activeValue !== matchValue) return;

        const overrideTemplate = menu.querySelector(templateSelector);
        const nextContent = overrideTemplate?.content?.querySelector(contentSelector);
        const currentContent = menu.querySelector(contentSelector);
        if (!nextContent || !currentContent) return;

        currentContent.replaceWith(nextContent.cloneNode(true));
        this.bindEvents(menu);
      });
    };

    if (includeTopLinksTemplate) {
      const topLinksTemplate = scope.querySelector?.('template[data-menu-drawer-deferred-template="top-links"]');
      if (topLinksTemplate?.content) applyToRoot(topLinksTemplate.content);
    }
    applyToRoot(scope);
  }

  getLastViewedHandlesFromStorage() {
    const storageKey = 'last_viewed_products';
    const ttlMs = 14 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return '';
      const list = JSON.parse(raw);
      if (!Array.isArray(list)) return '';
      return list
        .filter((item) => item && item.handle && item.viewedAt && now - item.viewedAt <= ttlMs)
        .map((item) => String(item.handle).trim())
        .filter(Boolean)
        .slice(0, 20)
        .join(',');
    } catch (error) {
      return '';
    }
  }

  refreshDynamicLists({ force = false, refreshToken = '' } = {}) {
    const menuDrawer = this.menuDrawerRoot || this.querySelector('#menu-drawer');
    if (!menuDrawer || menuDrawer.dataset.refreshDynamicLists !== 'true') return Promise.resolve(false);

    const sectionId = menuDrawer.dataset.sectionId;
    if (!sectionId) return Promise.resolve(false);

    const nextRefreshToken = refreshToken || this.getDynamicListsRefreshToken();
    if (!force && !this.dynamicListsNeedsRefresh && nextRefreshToken === this.dynamicListsLastRefreshToken) {
      return Promise.resolve(false);
    }

    if (this.dynamicListsRefreshInFlight) return this.dynamicListsRefreshInFlight;

    const requestUrl = this.buildHeaderSectionRequestUrl(sectionId);
    const lastViewedHandles = this.getLastViewedHandlesFromStorage();
    if (lastViewedHandles) {
      requestUrl.searchParams.set('rpd_lvh', lastViewedHandles);
    }

    this.dynamicListsRefreshInFlight = fetch(requestUrl.toString(), { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to refresh menu drawer lists (${response.status})`);
        return response.text();
      })
      .then((markup) => {
        const nextDocument = new DOMParser().parseFromString(markup, 'text/html');
        const nextDynamicLists = nextDocument.querySelector(this.dynamicListsSelector);
        const currentDynamicLists = menuDrawer.querySelector(this.dynamicListsSelector);

        if (nextDynamicLists) {
          const nextNode = nextDynamicLists.cloneNode(true);
          if (currentDynamicLists) {
            currentDynamicLists.replaceWith(nextNode);
          } else {
            const navigation = menuDrawer.querySelector('.menu-drawer__navigation');
            if (navigation) navigation.appendChild(nextNode);
          }
        } else if (currentDynamicLists) {
          currentDynamicLists.remove();
        }

        this.dynamicListsNeedsRefresh = false;
        this.dynamicListsLastRefreshToken = nextRefreshToken;
        return true;
      })
      .catch(() => {
        this.hydrateDeferredTemplate(menuDrawer, 'dynamic-lists');
        return false;
      })
      .finally(() => {
        this.dynamicListsRefreshInFlight = null;
      });

    return this.dynamicListsRefreshInFlight;
  }

  onSubmenuOpen(detailsElement) {
    if (detailsElement?.dataset?.menuDrawerLevel !== '1') return;
    this.updateDesktopAsideForSubmenu(detailsElement);
  }

  onSubmenuClosed(detailsElement) {
    if (detailsElement?.dataset?.menuDrawerLevel !== '1') return;
    this.restoreDefaultDesktopAside();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    this._lastBottom = null;
    this.onResize();
    this.header.classList.add('menu-open');
    const drawerContentReady = this.ensureDeferredDrawerContent();

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    drawerContentReady.then(() => {
      const navigation = this.menuDrawerRoot?.querySelector('.menu-drawer__navigation');
      const innerContainer = this.menuDrawerRoot?.querySelector('.menu-drawer__inner-container');
      const isDesktopDrawer = window.matchMedia('(min-width: 990px)').matches;
      if (navigation) {
        this.applyTopLinkOverrides(this.menuDrawerRoot);
        this.hydrateDeferredTemplate(navigation, 'top-links');
        if (!isDesktopDrawer) {
          this.hydrateDeferredTemplate(navigation, 'mobile-top-images');
        }
        if (isDesktopDrawer && innerContainer) {
          this.hydrateDeferredTemplate(innerContainer, 'desktop-images');
          this.cacheDefaultDesktopAside();
          this.restoreDefaultDesktopAside();
        }
        this.applyTopLinkOverrides(this.menuDrawerRoot);
      }

      if (this.menuDrawerRoot?.dataset.refreshDynamicLists === 'true') {
        this.refreshDynamicLists();
      } else {
        this.hydrateDeferredTemplate(this.menuDrawerRoot, 'dynamic-lists');
      }
    });
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.restoreDefaultDesktopAside();
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
    this._resizeRaf = requestAnimationFrame(() => {
      if (!this.header) return;
      const nextBottom = Math.floor(this.header.getBoundingClientRect().bottom - this.borderOffset);
      if (this._lastBottom !== nextBottom) {
        document.documentElement.style.setProperty('--header-bottom-position', `${nextBottom}px`);
        this._lastBottom = nextBottom;
      }
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    });
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('[id^="ModalClose-"], [data-modal-close]').forEach((closeButton) => {
      closeButton.addEventListener('click', this.hide.bind(this, false));
    });
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide(false);
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide(false);
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    const sectionEl = this.closest('.shopify-section');
    if (sectionEl && sectionEl.id) {
      this.dataset.section = sectionEl.id.replace('shopify-section-', '');
    } else {
      this.dataset.section = 'global';
    }
    document.body.appendChild(this);
  }

  show(opener) {
    const shouldLoadProductModalStyles =
      this.dataset.modalStylesLoaded !== 'true' &&
      (this.classList.contains('product-popup-modal') || this.classList.contains('product-media-modal')) &&
      typeof window.rpdEnsureStyles === 'function' &&
      window.themeStyleUrls?.productModals;

    if (shouldLoadProductModalStyles) {
      this.pendingOpenOpener = opener;
      if (!this.modalStylesPromise) {
        this.modalStylesPromise = window.rpdEnsureStyles([window.themeStyleUrls.productModals])
          .catch(() => {})
          .then(() => {
            this.dataset.modalStylesLoaded = 'true';
            const pendingOpener = this.pendingOpenOpener;
            this.pendingOpenOpener = null;
            this.modalStylesPromise = null;
            this.show(pendingOpener);
          });
      }
      return;
    }

    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    if (this.closeAnimationTimer) {
      window.clearTimeout(this.closeAnimationTimer);
      this.closeAnimationTimer = null;
    }
    if (this.closeAnimationTarget && this.closeAnimationHandler) {
      this.closeAnimationTarget.removeEventListener('transitionend', this.closeAnimationHandler);
      this.closeAnimationTarget = null;
      this.closeAnimationHandler = null;
    }
    this.classList.remove('is-closing');
    this.closing = false;
    if (typeof window.lockBodyScroll === 'function') {
      window.lockBodyScroll();
    } else {
      document.body.classList.add('overflow-hidden');
    }
    this.setAttribute('open', '');
    this.hydrateDeferredContent();
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide(focusBackToOpener = true) {
    if (!this.hasAttribute('open') || this.closing) return;

    const useClosingAnimation =
      this.classList.contains('product-popup-modal') || this.classList.contains('product-media-modal');
    const opener = this.openedBy;

    const finalizeHide = () => {
      this.closing = false;
      this.classList.remove('is-closing');
      this.closeAnimationTimer = null;
      this.closeAnimationTarget = null;
      this.closeAnimationHandler = null;
      if (typeof window.unlockBodyScroll === 'function') {
        window.unlockBodyScroll();
      } else {
        document.body.classList.remove('overflow-hidden');
      }
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      this.removeAttribute('open');
      window.pauseAllMedia();
      requestAnimationFrame(() => removeTrapFocus(focusBackToOpener ? opener : null));
    };

    if (!useClosingAnimation || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      requestAnimationFrame(finalizeHide);
      return;
    }

    this.closing = true;
    this.classList.add('is-closing');

    const animationTarget =
      this.querySelector('.product-popup-modal__content') || this.querySelector('.product-media-modal__content') || this;

    let hideFinalized = false;
    const finishOnce = () => {
      if (hideFinalized) return;
      hideFinalized = true;
      animationTarget.removeEventListener('transitionend', handleTransitionEnd);
      finalizeHide();
    };

    const handleTransitionEnd = (event) => {
      if (event.target !== animationTarget) return;
      if (event.propertyName !== 'opacity' && event.propertyName !== 'transform') return;
      finishOnce();
    };

    this.closeAnimationTarget = animationTarget;
    this.closeAnimationHandler = handleTransitionEnd;
    animationTarget.addEventListener('transitionend', handleTransitionEnd);
    this.closeAnimationTimer = window.setTimeout(finishOnce, 320);
  }

  hydrateDeferredContent() {
    this.querySelectorAll('[data-modal-deferred-image-container]').forEach((container) => {
      if (container.dataset.modalDeferredLoaded === 'true') return;

      const template = container.querySelector('template[data-modal-deferred-image-template]');
      const templateContent = template?.content?.firstElementChild;
      if (!templateContent) return;

      container.dataset.modalDeferredLoaded = 'true';
      container.appendChild(templateContent.cloneNode(true));
    });

    hydrateDeferredHtmlContainers(this);
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
    const trigger = this.querySelector('button') || this;
    if (trigger === this) {
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
    }

    const openModal = (event) => {
      if (event?.type === 'keydown' && !['Enter', ' ', 'Spacebar'].includes(event.key)) return;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal && typeof modal.show === 'function') {
        modal.show(trigger);
      } else if (modal) {
        modal.setAttribute('open', '');
      }
    };

    trigger.addEventListener('click', openModal);
    if (trigger === this) {
      this.addEventListener('keydown', openModal);
    }
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const template = this.querySelector('template');
      const templateContent = template?.content?.firstElementChild;
      if (!templateContent) return;

      this.setAttribute('loaded', true);
      const deferredElement = templateContent.cloneNode(true);
      this.appendChild(deferredElement);
      if (focus && typeof deferredElement.focus === 'function') deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      if (deferredElement.nodeName == 'IFRAME') {
        const formerStyle = deferredElement.getAttribute('style');
        deferredElement.setAttribute('style', 'display: block;');
        window.setTimeout(() => {
          if (formerStyle === null) {
            deferredElement.removeAttribute('style');
          } else {
            deferredElement.setAttribute('style', formerStyle);
          }
        }, 0);
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.onScrollHandler = this.onScroll.bind(this);
    this.slider.addEventListener('scroll', this.onScrollHandler, { passive: true });
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  onScroll() {
    if (this._scrollRaf) return;
    this._scrollRaf = requestAnimationFrame(() => {
      this._scrollRaf = null;
      this.update();
    });
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.onSlideshowScrollHandler = this.onSlideshowScroll.bind(this);
    this.slider.addEventListener('scroll', this.onSlideshowScrollHandler, { passive: true });
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  onSlideshowScroll() {
    if (this._slideshowScrollRaf) return;
    this._slideshowScrollRaf = requestAnimationFrame(() => {
      this._slideshowScrollRaf = null;
      this.setSlideVisibility();
    });
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.onVariantChange = (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    };
    this.onViewportResize = this.updateSquarePillSizing.bind(this);
  }

  connectedCallback() {
    this.addEventListener('change', this.onVariantChange);
    this.initializeSquarePillSizing();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onVariantChange);
    this.destroySquarePillSizing();
  }

  initializeSquarePillSizing() {
    this.updateSquarePillSizing();

    if ('ResizeObserver' in window) {
      this.squarePillResizeObserver = new ResizeObserver(() => this.updateSquarePillSizing());
      this.querySelectorAll('.product-form__input--pill-square .product-form__values').forEach((valuesContainer) => {
        this.squarePillResizeObserver.observe(valuesContainer);
      });
      return;
    }

    window.addEventListener('resize', this.onViewportResize);
  }

  destroySquarePillSizing() {
    this.squarePillResizeObserver?.disconnect();
    this.squarePillResizeObserver = null;
    window.removeEventListener('resize', this.onViewportResize);
  }

  updateSquarePillSizing() {
    this.querySelectorAll('.product-form__input--pill-square').forEach((fieldSet) => {
      const perRow = Number.parseFloat(fieldSet.style.getPropertyValue('--pill-per-row'));
      const valuesContainer = fieldSet.querySelector('.product-form__values');

      if (!valuesContainer || !perRow || perRow < 1) {
        fieldSet.style.removeProperty('--pill-square-size-px');
        return;
      }

      const valuesStyles = window.getComputedStyle(valuesContainer);
      const gap =
        Number.parseFloat(valuesStyles.columnGap) ||
        Number.parseFloat(valuesStyles.gap) ||
        Number.parseFloat(fieldSet.style.getPropertyValue('--pill-gap-x')) ||
        0;
      const containerWidth = valuesContainer.clientWidth;

      if (!containerWidth) return;

      const squareSize = (containerWidth - (perRow - 1) * gap) / perRow;
      if (!Number.isFinite(squareSize) || squareSize <= 0) return;

      fieldSet.style.setProperty('--pill-square-size-px', `${squareSize}px`);
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const selectedValue = target.closest('.product-form__input')?.querySelector('[data-selected-value]');
      if (selectedValue && !selectedValue.classList.contains('dropdown-swatch')) {
        selectedValue.textContent = target.selectedOptions[0].value;
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'))
      .map(({ dataset }) => dataset.optionValueId)
      .filter(Boolean);
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    const targetList = this.getTargetList();
    if (targetList) {
      this.loadIntoTargetList(productId, targetList);
      return;
    }

    const limit = this.getProductsLimit();
    const requestLimit = this.getRequestLimit(limit);
    const secondaryUrl = this.dataset.secondaryUrl;

    this.fetchRecommendations(this.dataset.url, productId, requestLimit)
      .then(({ html, recommendations, items }) => {
        const hasPrimaryContent = recommendations?.innerHTML.trim().length;
        const primaryCount = items.length;

        if (hasPrimaryContent) {
          this.innerHTML = recommendations.innerHTML;
          this.trimListToLimit(this.querySelector('ul'), limit);
        }

        if (!secondaryUrl || !limit || primaryCount >= limit) {
          this.applyRecommendationsState(html);
          return;
        }

        const remaining = Math.max(limit - primaryCount, 0);
        if (!remaining) {
          this.applyRecommendationsState(html);
          return;
        }

        return this.fetchRecommendations(secondaryUrl, productId, requestLimit)
          .then(({ html: secondaryHtml, recommendations: secondaryRec, items: secondaryItems }) => {
            const hasSecondaryContent = secondaryRec?.innerHTML.trim().length;

            if (!hasPrimaryContent && hasSecondaryContent) {
              this.innerHTML = secondaryRec.innerHTML;
              this.trimListToLimit(this.querySelector('ul'), limit);
              this.applyRecommendationsState(secondaryHtml);
              return;
            }

            if (hasPrimaryContent && hasSecondaryContent) {
              const list = this.querySelector('ul');
              if (!list) {
                this.applyRecommendationsState(html);
                return;
              }

              const existingKeys = new Set(
                Array.from(list.children)
                  .map((item) => this.getRecommendationKey(item))
                  .filter(Boolean)
              );

              secondaryItems.forEach((item) => {
                if (list.children.length >= limit) return;
                const key = this.getRecommendationKey(item);
                if (key && existingKeys.has(key)) return;
                if (key) existingKeys.add(key);
                list.appendChild(item);
              });
              this.trimListToLimit(list, limit);
            }

            this.applyRecommendationsState(hasPrimaryContent ? html : secondaryHtml);
          });
      })
      .catch((e) => {
        console.error(e);
      });
  }

  loadIntoTargetList(productId, targetList) {
    const configuredLimit = this.getProductsLimit();
    const targetLimit = this.getTargetLimit() || configuredLimit;
    const insertLimit = this.getTargetLimit() ? configuredLimit : null;
    const requestLimit = this.getRequestLimit(insertLimit || configuredLimit || targetLimit);
    const secondaryUrl = this.dataset.secondaryUrl;
    const fallbackList = this.getFallbackList();
    const modalTarget = this.dataset.modalTarget;
    const maxItems = Number.isFinite(targetLimit) ? targetLimit : Infinity;
    const insertBeforeTarget = this.getInsertBeforeTarget(targetList);
    this.removeUnavailableItems(targetList);
    const existingKeys = this.getExistingKeys(targetList);
    let insertedCount = 0;

    const appendItems = (items) => {
      insertedCount = this.appendItemsToList(targetList, items, {
        totalLimit: maxItems,
        insertLimit: Number.isFinite(insertLimit) ? insertLimit : null,
        insertedCount,
        existingKeys,
        insertBeforeTarget,
      });
    };

    const applyFallback = () => {
      if (!fallbackList || this.getRecommendationItemCount(targetList) >= maxItems) return;
      appendItems(Array.from(fallbackList.children));
    };

    this.fetchRecommendations(this.dataset.url, productId, requestLimit)
      .then(({ html, items }) => {
        appendItems(items);

        if (!secondaryUrl || this.getRecommendationItemCount(targetList) >= maxItems) {
          applyFallback();
          this.finalizeTargetList(targetList, insertBeforeTarget, maxItems);
          this.applyQuickAddTarget(targetList, modalTarget);
          this.applyRecommendationsState(html, targetList);
          return;
        }

        return this.fetchRecommendations(secondaryUrl, productId, requestLimit).then(({ html: secondaryHtml, items: secondaryItems }) => {
          appendItems(secondaryItems);
          applyFallback();
          this.finalizeTargetList(targetList, insertBeforeTarget, maxItems);
          this.applyQuickAddTarget(targetList, modalTarget);
          this.applyRecommendationsState(html || secondaryHtml, targetList);
        });
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getProductsLimit() {
    const limit = Number.parseInt(this.dataset.productsLimit, 10);
    return Number.isFinite(limit) && limit > 0 ? limit : null;
  }

  getRequestLimit(limit) {
    if (!Number.isFinite(limit) || limit <= 0) return null;
    return Math.min(limit * 2, 20);
  }

  getTargetList() {
    const selector = this.dataset.targetList;
    if (!selector) return null;
    return document.querySelector(selector);
  }

  getFallbackList() {
    const selector = this.dataset.fallbackList;
    if (!selector) return null;
    return document.querySelector(selector);
  }

  getTargetLimit() {
    const limit = Number.parseInt(this.dataset.targetLimit, 10);
    return Number.isFinite(limit) && limit > 0 ? limit : null;
  }

  getInsertBeforeTarget(list) {
    const selector = this.dataset.insertBefore;
    if (!selector || !list) return null;
    return list.querySelector(selector);
  }

  buildRecommendationsUrl(baseUrl, productId, limit) {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', this.dataset.sectionId);
    if (Number.isFinite(limit) && limit > 0) {
      url.searchParams.set('limit', limit);
    }
    return url.toString();
  }

  fetchRecommendations(baseUrl, productId, limit) {
    if (!baseUrl) {
      const empty = document.createElement('div');
      return Promise.resolve({ html: empty, recommendations: null, items: [] });
    }

    return fetch(this.buildRecommendationsUrl(baseUrl, productId, limit))
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');
        this.removeUnavailableItems(recommendations);
        const items = this.getRecommendationItems(recommendations);
        return { html, recommendations, items };
      });
  }

  getRecommendationItems(recommendations) {
    const list = recommendations?.querySelector('ul');
    if (!list) return [];
    return Array.from(list.children).filter((item) => item.tagName === 'LI');
  }

  getExistingKeys(list) {
    return new Set(
      Array.from(list.children)
        .map((item) => this.getRecommendationKey(item))
        .filter(Boolean)
    );
  }

  appendItemsToList(list, items, options = {}) {
    const {
      totalLimit = Infinity,
      insertLimit = null,
      insertedCount: initialInsertedCount = 0,
      existingKeys = new Set(),
      insertBeforeTarget = null,
    } = options;
    let insertedCount = initialInsertedCount;

    items.forEach((item) => {
      if (this.getRecommendationItemCount(list) >= totalLimit) return;
      if (Number.isFinite(insertLimit) && insertedCount >= insertLimit) return;
      if (!this.isAvailableItem(item)) return;
      const key = this.getRecommendationKey(item);
      if (key && existingKeys.has(key)) return;
      if (key) existingKeys.add(key);

      if (insertBeforeTarget && insertBeforeTarget.parentNode === list) {
        list.insertBefore(item, insertBeforeTarget);
      } else {
        list.appendChild(item);
      }

      insertedCount += 1;
    });

    return insertedCount;
  }

  applyQuickAddTarget(list, modalTarget) {
    if (!modalTarget) return;
    list.querySelectorAll('modal-opener').forEach((opener) => opener.setAttribute('data-modal', modalTarget));
    list.querySelectorAll('quick-add-modal').forEach((modal) => modal.remove());
  }

  getRecommendationKey(item) {
    const handle = item?.dataset?.productHandle;
    if (handle) return handle;

    const link = item?.querySelector('a[href]');
    if (!link) return null;

    const href = link.getAttribute('href');
    if (!href) return null;

    try {
      const url = new URL(href, window.location.origin);
      const pathname = url.pathname.replace(/\/+$/, '');
      const match = pathname.match(/\/products\/([^/]+)/);
      if (match && match[1]) return match[1];
      return pathname || href;
    } catch (e) {
      return href;
    }
  }

  isAvailableItem(item) {
    const availability = item?.querySelector('[data-product-available]');
    if (!availability) return true;
    return availability.getAttribute('data-product-available') !== 'false';
  }

  removeUnavailableItems(container) {
    if (!container) return;
    container.querySelectorAll('li').forEach((item) => {
      if (!this.isAvailableItem(item)) item.remove();
    });
  }

  trimListToLimit(list, limit) {
    if (!list || !Number.isFinite(limit) || limit <= 0) return;
    while (list.children.length > limit) {
      list.removeChild(list.lastElementChild);
    }
  }

  getRecommendationItemCount(list) {
    if (!list) return 0;
    return Array.from(list.children).filter((item) => item.tagName === 'LI' && !this.isAnchorItem(item)).length;
  }

  isAnchorItem(item) {
    return item?.hasAttribute?.('data-product-recommendations-anchor');
  }

  trimTargetListToLimit(list, limit) {
    if (!list || !Number.isFinite(limit) || limit <= 0) return;

    while (this.getRecommendationItemCount(list) > limit) {
      const removableItem = Array.from(list.children)
        .reverse()
        .find((item) => item.tagName === 'LI' && !this.isAnchorItem(item));
      if (!removableItem) break;
      removableItem.remove();
    }
  }

  finalizeTargetList(list, insertBeforeTarget, limit) {
    this.trimTargetListToLimit(list, limit);
    if (insertBeforeTarget?.parentNode) insertBeforeTarget.remove();

    const slider = list.closest('slider-component');
    if (slider && typeof slider.resetPages === 'function') {
      slider.resetPages();
    }
  }

  applyRecommendationsState(html, targetList = null) {
    if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
      this.remove();
      return;
    }

    if (targetList?.children?.length || html?.querySelector('.grid__item')) {
      this.classList.add('product-recommendations--loaded');
    }

    this.dispatchEvent(
      new CustomEvent('product-recommendations--loaded', {
        bubbles: true,
        detail: {
          hasItems: !!targetList?.children?.length || !!html?.querySelector('.grid__item'),
        },
      })
    );
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
